<script setup lang="ts">
import { useModalBannersStore } from '~/stores/apostatudo/modal-banners';
import { useResponsiveBannerUrl } from '~/composables/apostatudo/use-responsive-banner-url';
import { copyToClipboard } from '@/utils/apostatudo/clipboard';
import { shareNative } from '~/shared/NativeBridge';

const { t } = useTexts();
const layout = useLayoutStore();
const { fetchUserProfile, userAffiliationCode } = useUserData();
const { isUserRefersEnabled, isUserRefersComingSoon } = useFeatureFlagsJson();
const referTitle = 'Indique um amigo e ganhe';
const eligibleGamesButtonText = 'Ver jogos elegíveis';
const referBonusValue = 'R$ 30,00';
const referBetRequirement = 'R$ 150,00';
const referDescriptionHtml = `Ganhe ${referBonusValue} de Saldo real para cada amigo que você indicar para a apostatudo.bet.br. Para efetivar seu bônus, o seu amigo indicado deve <b class="text-white">acumular ${referBetRequirement} apostados em alguns jogos da PG Soft.</b> O usuário indicado tem 5 dias para completar o valor girado, contando a partir da data de cadastro. Jogos excluídos: Fortune Snake, Fortune Dragon, Futebol Fever, Chicky Run, Piggy Gold e Dragon Tiger Luck.`;

useSeo({
  title: referTitle,
});

const { isMobile } = useLayout();

const modalBannersStore = useModalBannersStore();

const { getResponsiveBannerUrl } = useResponsiveBannerUrl();

const indiqueBannerUrl = computed<string | null>(() => {
  const imageUrl = modalBannersStore.getImageUrl('indique', isMobile.value);

  return imageUrl ? getResponsiveBannerUrl(imageUrl) : null;
});

const isLinkCopied = ref<boolean>(false);

const affiliationLink = computed<string>(() => {
  return `https://${location.hostname}?ref=${userAffiliationCode.value}`;
});

async function handleCopyLink() {
  if (!affiliationLink.value) {
    return;
  }

  const ok = await copyToClipboard(affiliationLink.value);

  if (ok) {
    isLinkCopied.value = true;
    setTimeout(() => {
      isLinkCopied.value = false;
    }, 3000);
  } else {
    layout.openToaster({
      message: t('casino.game.sharing.not_supported'),
      type: 'error',
    });
  }
}

const handleShareLink = async () => {
  const title = `${t('account.refer_friend_come_play')} ${appName()}`;
  const url = affiliationLink.value;

  const outcome = await shareNative({ title, message: title, url });

  if (outcome === 'navigator') {
    layout.openToaster({
      message: t('casino.game.sharing.success'),
      type: 'success',
    });

    return;
  }

  if (outcome === 'native-app' || outcome === 'aborted') {
    return;
  }

  await navigateTo(`https://wa.me/?text=${title}: ${url}`, {
    external: true,
    open: {
      target: '_blank',
    },
  });
};

const handlePgSoftLink = async () => {
  await navigateTo('/games/providers/pgsoft?from_page=refer-friend', {
    external: false,
    open: {
      target: '_self',
    },
  });
};

onMounted(async () => {
  await fetchUserProfile();
  modalBannersStore.fetchBanner('indique', isMobile.value);
});

watch(
  [() => isUserRefersEnabled.value, () => isUserRefersComingSoon.value],
  ([enabled, comingSoon]) => {
    // Feature desligada ou "Em breve": sai da página pra home. O middleware
    // já bloqueia acesso direto pela URL; isso cobre navegação client-side.
    if (!enabled || comingSoon) {
      navigateTo('/');
    }
  },
  { immediate: true },
);
</script>

<template>
  <UserAccountPageLayout v-if="!isMobile">
    <div>
      <img
        v-if="indiqueBannerUrl"
        :alt="referTitle"
        class="w-full rounded-xl mb-4 object-cover"
        :src="indiqueBannerUrl"
      />

      <div>
        <div
          class="flex items-center justify-between text-white text-md px-4 py-2 font-semibold bg-[#0D233C] rounded-t-xl min-h-[44px]"
        >
          <div class="flex">
            <NuxtIcon
              class="mr-3 text-[18px]"
              name="dollar"
            />
            <p>
              Ganhe <b class="text-[#00ff00]">{{ referBonusValue }}</b> de
              <b>SALDO REAL</b>
            </p>
          </div>

          <div
            class="bg-[#1B3655] text-[#CBDBEC] py-1 px-4 rounded-lg text-[12px] hover:opacity-70 cursor-pointer flex items-center gap-2"
            @click="handlePgSoftLink()"
          >
            <NuxtIcon
              class="text-[12px]"
              name="info"
            />
            <span>{{ eligibleGamesButtonText }}</span>
          </div>
        </div>
        <div
          class="flex items-center text-[#9DBCDC] font-[15px] bg-[#0A192A] px-4 py-2 pb-4 rounded-b-xl w-full gap-6"
        >
          <template v-if="userAffiliationCode">
            <p
              class="text-sm basis-1/2"
              v-html="referDescriptionHtml"
            ></p>
            <div class="basis-1/2">
              <BaseFormInput
                v-if="!isMobile"
                class="h-[50px] bg-[#0B2540] text-white/90 rounded-lg w-full"
                :initial-value="affiliationLink"
                :model-value="affiliationLink"
                readonly
                type="text"
              />

              <div class="flex gap-2 mt-2">
                <button
                  class="flex items-center justify-center gap-3 rounded-lg px-4 py-2 bg-gradient-to-b from-[#01b7fc] to-[#017fd5] h-[50px] text-white font-semibold text-[14px] basis-[85%]"
                  @click="handleCopyLink()"
                >
                  <template v-if="isLinkCopied">
                    <NuxtIcon
                      class="text-[18px]"
                      name="check"
                    />
                    <span>{{ $t('account.copied_link') }}</span>
                  </template>
                  <template v-else>
                    <NuxtIcon
                      class="text-[18px]"
                      name="copy"
                    />
                    <span>{{ $t('account.copy_link') }}</span>
                  </template>
                </button>

                <button
                  class="flex items-center justify-center gap-3 rounded-lg px-4 py-2 bg-gradient-to-b from-[#ffffff] to-[#9ba0a3] h-[50px] text-white font-semibold text-[14px] basis-[15%]"
                  @click="handleShareLink()"
                >
                  <NuxtIcon
                    class="text-[18px] text-[#000]"
                    filled
                    name="share"
                  />
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <UserMyReferralsSingle />

    <UserRefersTerms />
  </UserAccountPageLayout>

  <UserAccountPageLayout
    v-else-if="isMobile"
    :title="referTitle"
  >
    <div>
      <img
        v-if="indiqueBannerUrl"
        :alt="referTitle"
        class="w-full rounded-xl mb-4 object-cover"
        :src="indiqueBannerUrl"
      />

      <div
        class="flex items-center text-white text-md px-4 py-2 font-semibold bg-[#0D233C] rounded-t-xl h-[44px]"
      >
        <NuxtIcon
          class="mr-3 text-[18px]"
          name="dollar"
        />
        <p>
          Ganhe <b class="text-[#00ff00]">{{ referBonusValue }}</b> de
          <b>SALDO REAL</b>
        </p>
      </div>

      <div
        class="text-[#9DBCDC] font-[15px] bg-[#0A192A] px-4 py-2 rounded-b-xl pb-4"
      >
        <template v-if="userAffiliationCode">
          <p
            class="text-sm"
            v-html="referDescriptionHtml"
          ></p>

          <hr class="border-t border-white/30 my-3" />

          <div
            class="bg-[#1B3655] text-[#CBDBEC] py-2 px-4 rounded-lg text-[12px] hover:opacity-70 cursor-pointer flex items-center justify-center gap-2 mb-2"
            @click="handlePgSoftLink()"
          >
            <NuxtIcon
              class="text-[12px]"
              name="info"
            />
            <span>{{ eligibleGamesButtonText }}</span>
          </div>

          <div class="flex gap-2 w-full">
            <button
              class="flex items-center justify-center gap-3 rounded-lg px-4 py-2 bg-gradient-to-b from-[#01b7fc] to-[#017fd5] h-[50px] text-white font-semibold text-[14px] basis-[85%]"
              @click="handleCopyLink()"
            >
              <template v-if="isLinkCopied">
                <NuxtIcon
                  class="text-[18px]"
                  name="check"
                />
                <span>{{ $t('account.copied_link') }}</span>
              </template>
              <template v-else>
                <NuxtIcon
                  class="text-[18px]"
                  name="copy"
                />
                <span>{{ $t('account.copy_link') }}</span>
              </template>
            </button>

            <button
              class="flex items-center justify-center gap-3 rounded-lg px-4 py-2 bg-gradient-to-b from-[#ffffff] to-[#9ba0a3] h-[50px] text-white font-semibold text-[14px] basis-[15%]"
              @click="handleShareLink()"
            >
              <NuxtIcon
                class="text-[18px] text-[#000]"
                filled
                name="share"
              />
            </button>
          </div>
        </template>
      </div>
    </div>

    <UserMyReferralsSingle />

    <UserRefersTerms />
  </UserAccountPageLayout>
</template>
